import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api/client';
import { ApiError } from '../api/errors';
import type { components } from '../api/schema';
import { unwrap } from '../api/unwrap';
import { buildListQuerySerializer } from '../lib/list-query';
import { pruneUndefined } from '../lib/prune';

import type { AccountCreateInput, AccountRole, AccountStatus, AccountUpdateInput } from './schemas';

type CreateAccountRequest = components['schemas']['CreateAccountRequest'];
type UpdateAccountRequest = components['schemas']['UpdateAccountRequest'];
export type ActiveStatsRecord = components['schemas']['ActiveStatsResponse'];
export type InventorySnapshot = components['schemas']['InventoryResponse'];

export const accountsKeys = {
  all: ['accounts'] as const,
  list: (filter: { status?: AccountStatus; includeDeleted: boolean }) =>
    ['accounts', 'list', filter] as const,
  one: (id: string) => ['accounts', 'one', id] as const,
};

export type ProxyHealth = 'ok' | 'soft' | 'down';

export interface AccountRecord {
  id: string;
  steamLogin: string;
  steamId64: string;
  email: string | null;
  // Phase 10 — storage / sender split (Plan 10-01). Drives the
  // role-aware UI columns + replenishment-worker partitioning.
  role: AccountRole;
  status: AccountStatus;
  availableAfter: string;
  // Plan 18-01 — active trade cap (replaces daily-cap). Per-account ceiling
  // on concurrent in-flight trades (status IN queued|sending|sent). Slot
  // frees on terminal state — no time-based reset.
  maxActiveTrades: number;
  activeTrades: number;
  purchasedAt: string | null;
  // Phase 8.5-04 SEC-01 (ADR-014). Single derived flag exposing «есть operable
  // credentials». Always `true` for live accounts in current schema (NOT NULL
  // constraints); explicit contract field for future stub-account scenarios.
  credentialsPresent: boolean;
  hasRefreshToken: boolean;
  hasEmailPassword: boolean;
  lastRefreshAt: string | null;
  // Plan 08-03 — proxy LEFT JOIN extension. `proxyId IS NULL` accounts get
  // `null` for both fields. Health is derived backend-side from
  // proxy_ok / proxy_ok_soft.
  proxyUrl: string | null;
  proxyHealth: ProxyHealth | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

// Phase 8.5-04 SEC-02 — credentials rotation request body. ADR-014.
export interface RotateCredentialsInput {
  password: string;
  sharedSecret: string;
  identitySecret: string;
  revocationCode?: string | null;
}

export interface AccountsImportResult {
  imported: number;
  failed: { file: string; reason: string }[];
}

// Phase 8.5-05 — server-side URL-driven list (ADR-014). Bracketed filter
// + signed sort + 1-indexed page; backend returns `{ items, page,
// pageSize, total }`.
export type AccountSort = 'createdAt' | 'steamLogin' | 'status';

export interface AccountsListFilter {
  role?: AccountRole | undefined;
  status?: AccountStatus | undefined;
  includeDeleted?: boolean | undefined;
}

export interface AccountsListInput {
  filter: AccountsListFilter;
  sort: { field: AccountSort | null; dir: 'asc' | 'desc' };
  page: number;
  pageSize: number;
}

export interface AccountsListResult {
  items: AccountRecord[];
  page: number;
  pageSize: number;
  total: number;
}

const formatAccountSort = (sort: AccountsListInput['sort']): string => {
  const field = sort.field ?? 'createdAt';
  return sort.dir === 'desc' ? `-${field}` : field;
};

export const useAccounts = (input: AccountsListInput) =>
  useQuery({
    queryKey: ['accounts', 'list', input] as const,
    queryFn: async (): Promise<AccountsListResult> => {
      const filter: { role?: AccountRole; status?: AccountStatus; includeDeleted?: 'true' } = {};
      if (input.filter.role) filter.role = input.filter.role;
      if (input.filter.status) filter.status = input.filter.status;
      if (input.filter.includeDeleted) filter.includeDeleted = 'true';
      const result = await api.GET('/api/accounts', {
        params: {
          query: {
            sort: formatAccountSort(input.sort),
            page: String(input.page),
            pageSize: String(input.pageSize),
          },
        },
        querySerializer: buildListQuerySerializer(filter),
      });
      return unwrap(result) as AccountsListResult;
    },
  });

export const useAccount = (id: string | undefined) =>
  useQuery({
    queryKey: id ? accountsKeys.one(id) : ['accounts', 'one', 'pending'],
    enabled: Boolean(id),
    queryFn: async (): Promise<AccountRecord> => {
      if (!id) throw new Error('account id required');
      const result = await api.GET('/api/accounts/{id}', { params: { path: { id } } });
      return unwrap(result).account;
    },
  });

const invalidateAccounts = (qc: ReturnType<typeof useQueryClient>): Promise<void> =>
  qc.invalidateQueries({ queryKey: accountsKeys.all });

export const useCreateAccount = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: AccountCreateInput): Promise<AccountRecord> => {
      const payload = pruneUndefined({
        ...body,
        email: body.email === undefined || body.email === '' ? null : body.email,
      }) as CreateAccountRequest;
      const result = await api.POST('/api/accounts', { body: payload });
      return unwrap(result).account;
    },
    onSuccess: () => invalidateAccounts(qc),
  });
};

export const useUpdateAccount = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: AccountUpdateInput): Promise<AccountRecord> => {
      const payload = pruneUndefined({
        ...body,
        email: body.email === '' ? null : body.email,
      }) as UpdateAccountRequest;
      const result = await api.PATCH('/api/accounts/{id}', {
        params: { path: { id } },
        body: payload,
      });
      return unwrap(result).account;
    },
    onSuccess: () => invalidateAccounts(qc),
  });
};

export const useDeleteAccount = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const result = await api.DELETE('/api/accounts/{id}', { params: { path: { id } } });
      unwrap(result);
    },
    onSuccess: () => invalidateAccounts(qc),
  });
};

// Phase 8.5-04 UX-01 — tier-3 hard-delete (ADR-014). Body confirmation must
// equal account.steamLogin exactly. Server-side: 400 confirmation_mismatch on
// miss, 403 if non-admin, 409 if non-terminal trades exist; trades.account_id
// nulls out (migration 0017 SET NULL); account_proxies cascade-clear.
export const useDestroyAccount = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; confirmation: string }): Promise<void> => {
      const result = await api.POST('/api/accounts/{id}/destroy', {
        params: { path: { id: args.id } },
        body: { confirmation: args.confirmation },
      });
      unwrap(result);
    },
    onSuccess: () => invalidateAccounts(qc),
  });
};

// Phase 8.5-04 SEC-02 — admin-only credentials rotation (ADR-014). Server-side
// re-encrypts each field with current key version; audit logs metadata
// (rotated-fields list) only — no plaintext or hashes.
export const useRotateCredentials = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      id: string;
      payload: RotateCredentialsInput;
    }): Promise<{
      account: AccountRecord;
      rotatedFields: string[];
    }> => {
      const result = await api.POST('/api/accounts/{id}/credentials/rotate', {
        params: { path: { id: args.id } },
        body: args.payload,
      });
      const body = unwrap(result) as { account: AccountRecord; rotatedFields: string[] };
      return body;
    },
    onSuccess: () => invalidateAccounts(qc),
  });
};

export const useImportAccountsFromTradeOn = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File): Promise<AccountsImportResult> => {
      const formData = new FormData();
      formData.append('archive', file);
      const response = await fetch('/api/accounts/import-tradeon', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (!response.ok) {
        const text = await response.text();
        let code = text;
        try {
          const parsed = JSON.parse(text) as { error?: string };
          code = parsed.error ?? text;
        } catch {
          // ignore
        }
        throw new ApiError(response.status, code || 'import_failed');
      }
      return (await response.json()) as AccountsImportResult;
    },
    onSuccess: () => invalidateAccounts(qc),
  });
};

export interface SessionHealthRecord {
  alive: boolean;
  hasRefreshToken: boolean;
  lastRefreshAt: string | null;
  stale: boolean;
  thresholdDays: number;
}

export const useSessionHealth = (id: string | undefined) =>
  useQuery({
    queryKey: id ? ['accounts', 'one', id, 'session-health'] : ['accounts', 'pending', 'health'],
    enabled: Boolean(id),
    queryFn: async (): Promise<SessionHealthRecord> => {
      if (!id) throw new Error('id required');
      const result = await api.GET('/api/accounts/{id}/session-health', {
        params: { path: { id } },
      });
      return unwrap(result);
    },
  });

// Plan 18-01 — active-stats (replaces daily-stats). Reads the live
// `accounts.active_trades` counter from PG; refresh every 30s so the
// «активных / лимит» badge stays roughly current.
export const useAccountActiveStats = (id: string | undefined) =>
  useQuery({
    queryKey: id
      ? ['accounts', 'one', id, 'active-stats']
      : ['accounts', 'pending', 'active-stats'],
    enabled: Boolean(id),
    refetchInterval: 30_000,
    queryFn: async (): Promise<ActiveStatsRecord> => {
      if (!id) throw new Error('id required');
      const result = await api.GET('/api/accounts/{id}/active-stats', {
        params: { path: { id } },
      });
      return unwrap(result);
    },
  });

// Plan 10-03 — storage inventory snapshot. Cache TTL is server-side
// (60s default in Redis); the React Query cache keeps the same window
// to match. Manual refetch via `inventoryQuery.refetch()` from the UI.
//
// Phase 19 SB-19b — default combo 753:6 (Steam Community) matches
// server-side default (changed from 730:2 CS2 — empty in prod).
export const useAccountInventory = (
  id: string | undefined,
  opts?: { appid?: number; contextId?: number; enabled?: boolean },
) =>
  useQuery({
    queryKey: id
      ? ['accounts', 'one', id, 'inventory', opts?.appid ?? 753, opts?.contextId ?? 6]
      : ['accounts', 'pending', 'inventory'],
    enabled: Boolean(id) && (opts?.enabled ?? true),
    staleTime: 60_000,
    queryFn: async (): Promise<InventorySnapshot> => {
      if (!id) throw new Error('id required');
      const query: { appid?: string; contextId?: string } = {};
      if (opts?.appid !== undefined) query.appid = String(opts.appid);
      if (opts?.contextId !== undefined) query.contextId = String(opts.contextId);
      const result = await api.GET('/api/accounts/{id}/inventory', {
        params: { path: { id }, query },
      });
      return unwrap(result);
    },
  });
